import {FC} from 'react';
import {Box, Typography} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {ProviderRequestLog} from '@entities/transaction';
import {MrTable} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {formatInTz} from '@shared/utils';

import {COLOR__LIGHT_BACK} from '@/theme/colors';

const columns: MRT_ColumnDef<ProviderRequestLog>[] = [
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: ({row}) => formatInTz(row.original.created, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'direction',
    header: 'Direction',
    size: 110,
  },
  {
    accessorKey: 'provider',
    header: 'Provider',
    size: 120,
  },
  {
    accessorKey: 'method',
    header: 'Method',
    size: 90,
  },
  {
    accessorKey: 'statusCode',
    header: 'Status',
    size: 90,
  },
  {
    accessorKey: 'endpoint',
    header: 'Endpoint',
    size: 180,
  },
  {
    accessorKey: 'url',
    header: 'URL',
    size: 320,
  },
];

const BodyBlock = ({title, body}: {title: string; body: object | null}) => (
  <Box flex={1} minWidth={280}>
    <Typography component="div" color="text.secondary" mb={0.5}>
      {title}
    </Typography>
    <Box
      component="pre"
      m={0}
      p={1.5}
      bgcolor={COLOR__LIGHT_BACK}
      borderRadius="6px"
      sx={{
        fontSize: 12,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {body ? JSON.stringify(body, null, 2) : '—'}
    </Box>
  </Box>
);

interface Props {
  logs: ProviderRequestLog[];
  isLoading: boolean;
  error: Error | null;
}

export const ProviderLogsTable: FC<Props> = ({logs, isLoading, error}) => (
  <MrTable
    columns={columns}
    data={logs}
    error={error}
    // Request and response bodies are too big for cells
    renderDetailPanel={({row}) => (
      <Box display="flex" gap={2} flexWrap="wrap">
        <BodyBlock title="Request body" body={row.original.requestBody} />
        <BodyBlock title="Response body" body={row.original.responseBody} />
      </Box>
    )}
    state={{isLoading}}
    enablePagination={false}
    {...entityTableCommonProps}
    enableRowActions={false}
  />
);
